import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  getArticleItems,
  getProducts,
  getServices,
  parsePath,
  resolvePage
} from "./lib/content";
import { siteContent } from "./content/site-content.js";
import { setSeo } from "./lib/seo";
import { Nav } from "./components/Nav.jsx";
import { Footer } from "./components/Footer.jsx";
import { Home } from "./pages/Home.jsx";
import { Services } from "./pages/Services.jsx";
import { Products } from "./pages/Products.jsx";
import { Articles } from "./pages/Articles.jsx";
import { Careers } from "./pages/Careers.jsx";
import { Contact } from "./pages/Contact.jsx";
import { ResolvedPage } from "./pages/ResolvedPage.jsx";
import { NotFound } from "./pages/NotFound.jsx";
import "./styles/main.css";

const RADIUS = 16;

/* URL slug <-> page id. "contact" page lives at the /contact-us slug. */
const SLUG_TO_PAGE = {
  home: "home",
  services: "services",
  products: "products",
  articles: "articles",
  careers: "careers",
  "contact-us": "contact"
};
const PAGE_TO_SLUG = {
  home: "",
  services: "services",
  products: "products",
  articles: "articles",
  careers: "careers",
  contact: "contact-us"
};
const PAGE_TITLES = {
  home: "Rework Ltd",
  services: "Services · Rework Ltd",
  products: "Products · Rework Ltd",
  articles: "Articles · Rework Ltd",
  careers: "Careers · Rework Ltd",
  contact: "Contact us · Rework Ltd"
};

function App() {
  const [route, setRoute] = useState(() => parsePath(window.location.pathname));
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState(() => siteContent[route.locale].services);
  const [products, setProducts] = useState(() => siteContent[route.locale].products);
  const [articleItems, setArticleItems] = useState(() => siteContent[route.locale].articlesPage.items.slice(0, 10));
  const [articleTotal, setArticleTotal] = useState(() => siteContent[route.locale].articlesPage.items.length);

  const { locale, slug } = route;
  const prefix = locale === "th" ? "/th" : "";
  const pageId = SLUG_TO_PAGE[slug] || null;

  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Marketing data: seed static for the locale, then let Directus override.
  useEffect(() => {
    let cancelled = false;
    setServices(siteContent[locale].services);
    setProducts(siteContent[locale].products);
    setArticleItems(siteContent[locale].articlesPage.items.slice(0, 10));
    setArticleTotal(siteContent[locale].articlesPage.items.length);
    Promise.all([getServices(locale), getProducts(locale), getArticleItems(locale)]).then(
      ([s, p, a]) => {
        if (cancelled) return;
        setServices(s);
        setProducts(p);
        setArticleItems(a.items);
        setArticleTotal(a.total);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [locale]);

  // Non-marketing slugs resolve against Directus / scraped fallback.
  useEffect(() => {
    if (pageId) {
      setResolved(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    resolvePage(slug, locale).then((page) => {
      if (cancelled) return;
      setResolved(page);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, locale, pageId]);

  const c = {
    ...siteContent[locale],
    services,
    products,
    articlesPage: { ...siteContent[locale].articlesPage, items: articleItems, total: articleTotal, pageSize: 10 }
  };

  const path = window.location.pathname || "/";
  useEffect(() => {
    setSeo({
      title: resolved?.data?.seoTitle || resolved?.data?.title || PAGE_TITLES[pageId] || "Rework Ltd",
      description: resolved?.data?.seoDescription || resolved?.data?.description || c.home.lede,
      locale,
      path
    });
  }, [path, resolved, locale, pageId, c.home.lede]);

  function navigate(href) {
    window.history.pushState({}, "", href);
    setRoute(parsePath(window.location.pathname));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // go(pageId) used inside ported pages -> real URL navigation.
  function go(id) {
    const target = PAGE_TO_SLUG[id] ?? id;
    navigate(target ? `${prefix}/${target}` : prefix || "/");
  }

  // Locale toggle keeps the current slug, swaps the /th prefix.
  function setLocale(lc) {
    const newPrefix = lc === "th" ? "/th" : "";
    const rest = slug === "home" ? "" : slug;
    navigate(rest ? `${newPrefix}/${rest}` : newPrefix || "/");
  }

  let page;
  if (pageId === "home") page = <Home go={go} c={c} radius={RADIUS} />;
  else if (pageId === "services") page = <Services go={go} c={c} radius={RADIUS} />;
  else if (pageId === "products") page = <Products go={go} c={c} radius={RADIUS} />;
  else if (pageId === "articles") page = <Articles go={go} c={c} locale={locale} radius={RADIUS} />;
  else if (pageId === "careers") page = <Careers go={go} c={c} radius={RADIUS} />;
  else if (pageId === "contact") page = <Contact c={c} radius={RADIUS} />;
  else if (loading) page = <Loading c={c} />;
  else if (resolved) page = <ResolvedPage go={go} c={c} resolved={resolved} radius={RADIUS} />;
  else page = <NotFound go={go} c={c} radius={RADIUS} />;

  return (
    <>
      <Nav page={pageId || ""} go={go} locale={locale} setLocale={setLocale} nav={c.nav} />
      {page}
      <Footer go={go} footer={c.footer} />
    </>
  );
}

function Loading({ c }) {
  return (
    <main style={{ fontFamily: "var(--font-sans)" }}>
      <section style={{ padding: "clamp(4rem, 10vw, 8rem) 0" }}>
        <div style={{ width: "100%", maxWidth: "var(--container-max)", margin: "0 auto", padding: "0 var(--gutter)" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--brand-red)", fontSize: "var(--text-xs)" }}>
            {c.nav.localeName === "TH" ? "กำลังโหลด" : "Loading"}
          </div>
          <h1 style={{ margin: "12px 0 0", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(2rem, 5vw, var(--text-3xl))", color: "var(--brand-ink)" }}>Rework Ltd</h1>
        </div>
      </section>
    </main>
  );
}

createRoot(document.getElementById("root")).render(<App />);
